import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { LocalPageCombinedState, LocalPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { LocalPageDataContextProvider } from './pageData';
import { LocalPagePaginatedDecisionMakersDataContextProvider } from './pageDecisionMakersPaginatedData';
import { LocalPagePaginatedPetitionUpdatesDataContextProvider } from './pagePetitionUpdatesPaginatedData';
import { LocalPagePaginatedRecentPetitionsDataContextProvider } from './pageRecentPetitionsPaginatedData';
import { LocalPagePaginatedTrendingPetitionsDataContextProvider } from './pageTrendingPetitionsPaginatedData';
import { LocalPagePaginatedVictoriousPetitionsDataContextProvider } from './pageVictoriousPetitionsPaginatedData';

export type ErrorHandlerProps = { context: ErrorStateDetails<LocalPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: LocalPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const LocalPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const {
		pageData,
		decisionMakersPaginatedData,
		petitionUpdatesPaginatedData,
		recentPetitionsPaginatedData,
		trendingPetitionsPaginatedData,
		victoriousPetitionsPaginatedData,
	} = pageContextState;

	return (
		<LocalPageDataContextProvider pageData={pageData}>
			<LocalPagePaginatedRecentPetitionsDataContextProvider recentPetitionsPaginatedData={recentPetitionsPaginatedData}>
				<LocalPagePaginatedTrendingPetitionsDataContextProvider
					trendingPetitionsPaginatedData={trendingPetitionsPaginatedData}
				>
					<LocalPagePaginatedVictoriousPetitionsDataContextProvider
						victoriousPetitionsPaginatedData={victoriousPetitionsPaginatedData}
					>
						<LocalPagePaginatedDecisionMakersDataContextProvider
							decisionMakersPaginatedData={decisionMakersPaginatedData}
						>
							<LocalPagePaginatedPetitionUpdatesDataContextProvider
								petitionUpdatesPaginatedData={petitionUpdatesPaginatedData}
							>
								<TrackingContext
									properties={{
										location_name: pageData.data.displayName,
										petition_count: trendingPetitionsPaginatedData.data.totalCount,
										signature_count: pageData.data.totalSignatureCount,
									}}
								>
									{children}
								</TrackingContext>
							</LocalPagePaginatedPetitionUpdatesDataContextProvider>
						</LocalPagePaginatedDecisionMakersDataContextProvider>
					</LocalPagePaginatedVictoriousPetitionsDataContextProvider>
				</LocalPagePaginatedTrendingPetitionsDataContextProvider>
			</LocalPagePaginatedRecentPetitionsDataContextProvider>
		</LocalPageDataContextProvider>
	);
};
